import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { createFeatureSelector, createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { FullscreenLayoutAreas, layoutTemplatesAreas } from '@src/assets/css/fullscreen.template.scss';
import { GroupKey } from '@src/suite/common/data-models/search-results/group-key';
import { ResultGroup } from '@src/suite/common/data-models/search-results/result-group';
import { TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { SYSTEM_VS_GUID } from '@src/suite/common/enums/constants';
import { SearchType } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BASE_TEMPLATE_CLASS, FullscreenNewRecordState, FullscreenState, SidecarMenu, SidecarState } from '@src/suite/common/store/fullscreen/models/fullscreen.models';
import { selectMosDevices } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectListGuids } from '@src/suite/common/store/list/list.selectors';
import { getIsNodeExpandable, getNodeChildren, getNodeLevel, getRecordsTreeLikeData, nodeTransformer } from '@src/suite/common/store/playbook/playbook.utils';
import {
    selectAssignmentRecordsMap,
    selectRundownRecordsMap,
    selectRundownsWithStories,
    selectStoryRecordsMap,
    selectTopicRecordsMap,
    selectTopicsWithAssignmentsAndStories,
} from '@src/suite/common/store/records/records.selectors';

const sidecarMenus = new Map<ObjectTypes, SidecarMenu[]>([
    [ObjectTypes.SCRIPT, [SidecarMenu.TOPICS, SidecarMenu.ASSIGNMENTS, SidecarMenu.STORYPROPERTIES, SidecarMenu.MOS, SidecarMenu.AP_AI_SEARCH, SidecarMenu.RUNDOWNS, SidecarMenu.STORIES]],
    [ObjectTypes.TOPIC, [SidecarMenu.TOPICS, SidecarMenu.ASSIGNMENTS, SidecarMenu.STORYPROPERTIES, SidecarMenu.MOS, SidecarMenu.AP_AI_SEARCH, SidecarMenu.RUNDOWNS, SidecarMenu.STORIES]],
    [ObjectTypes.RUNDOWN, [SidecarMenu.TOPICS, SidecarMenu.ASSIGNMENTS, SidecarMenu.STORYPROPERTIES, SidecarMenu.MOS, SidecarMenu.AP_AI_SEARCH, SidecarMenu.RUNDOWNS, SidecarMenu.STORIES]],
]);

const selectFullscreenState = createFeatureSelector<FullscreenState>('fullscreenDialog');
export const selectFullscreenNewRecordDialogState = createFeatureSelector<FullscreenNewRecordState>('fullscreenNewRecordDialog');

export const selectFullscreenNewRecord = createSelector(selectFullscreenNewRecordDialogState, state => {
    const record = state.record;
    if (record) {
        record.isNewRecord = true;
    }
    return record;
});

export const selectFullscreenTreeDataSourceControlPair = createSelector(selectFullscreenState, state => state.treeDataPair || null);
export const selectNewRecordCreationInProgress = createSelector(selectFullscreenNewRecordDialogState, state => state.recordCreating);

export const selectLoaderState = createSelector(selectFullscreenState, state => {
    return state.showLoader;
});

export const selectLayoutTemplate = createSelector(selectFullscreenState, (state: FullscreenState) => {
    return state.template;
});

export const selectSidecarVisible = (key: FullscreenLayoutAreas) =>
    createSelector(selectLayoutTemplate, template => {
        return layoutTemplatesAreas[template]?.has(key);
    });

export const selectLayoutTemplateClass = createSelector(selectLayoutTemplate, template => {
    return `${BASE_TEMPLATE_CLASS}--${template}`;
});

export const selectFullscreenRecordGuid = createSelector(selectFullscreenState, state => {
    return state.recordGuid;
});

export const selectFullscreenRecordType = createSelector(selectFullscreenState, state => {
    return state.recordType;
});

/**
 * Standalone story is if the current fullscreen state has standaloneStory set,
 * or if we are in 'new record' mode, and the record is a script
 */
export const selectIsCurrentStoryStandalone = createSelector(selectFullscreenState, selectFullscreenNewRecordDialogState, (state, newRecordState) => {
    return state.standaloneStory || newRecordState?.record?.Type === ObjectTypes.SCRIPT;
});

export const selectParentRecord = createSelector(
    selectFullscreenState,
    selectFullscreenNewRecord,
    selectFullscreenRecordGuid,
    selectTopicsWithAssignmentsAndStories,
    selectRundownsWithStories,
    selectAssignmentRecordsMap,
    selectStoryRecordsMap,
    (state, newTopic, guid, topics, rundowns, assignments, scripts) => {
        if (newTopic) {
            newTopic.isNewRecord = true;
            return newTopic as StorytellingTopicRecord;
        }
        if (state.recordType === ObjectTypes.SCRIPT) {
            const script = <StorytellingStoryRecord>scripts.get(guid);
            guid = script?.PSTStoryTopicGUID;
        }
        if (state.recordType === ObjectTypes.ASSIGNMENT) {
            const assignment = <StorytellingAssignmentRecord>assignments.get(guid);
            guid = assignment.topicMetadata?.Guid;
        }
        if (state.recordType === ObjectTypes.RUNDOWN) {
            return rundowns.get(guid);
        }
        return topics.get(guid);
    }
);

const selectFullscreenStoriesMap = createSelector(selectFullscreenNewRecord, selectStoryRecordsMap, (newRecord, stories) => {
    if (newRecord && newRecord.Type === ObjectTypes.TOPIC) {
        return new Map<string, StorytellingStoryRecord>((newRecord as StorytellingTopicRecord).stories?.map(story => [story.Guid, { ...story, isNewRecord: true }]));
    }
    return stories;
});

export const selectAllStoriesMap = createSelector(selectStoryRecordsMap, selectFullscreenStoriesMap, (stories, newStories) => {
    return new Map<string, StorytellingStoryRecord>([...stories, ...newStories]);
});

export const selectStoryRecord = createSelector(selectFullscreenRecordGuid, selectFullscreenStoriesMap, (guid, stories) => {
    return stories.get(guid);
});

const selectFullscreenAssignmentsMap = createSelector(selectFullscreenNewRecordDialogState, selectAssignmentRecordsMap, (state, assignments) => {
    if (state.record && state.record.Type === ObjectTypes.TOPIC) {
        return new Map<string, StorytellingAssignmentRecord>((state.record as StorytellingTopicRecord).children?.map(assignment => [assignment.Guid, { ...assignment, isNewRecord: true }]));
    }
    return assignments;
});

const selectAllAssignmentsMap = createSelector(selectAssignmentRecordsMap, selectFullscreenAssignmentsMap, (assignments, newAssignments) => {
    return new Map<string, StorytellingAssignmentRecord>([...assignments, ...newAssignments]);
});

export const selectAssignmentRecord = createSelector(selectFullscreenRecordGuid, selectFullscreenAssignmentsMap, (guid, assignments) => {
    return assignments.get(guid);
});

export const selectRundownRecord = createSelector(selectFullscreenRecordGuid, selectRundownsWithStories, (guid, rundowns) => {
    return rundowns.get(guid);
});

// TODO Current Stories SELECTOR
// TODO Current Topic SELECTOR
// TODO Current Rundown SELECTOR
export const selectCurrentRecord = createSelector(
    selectFullscreenNewRecordDialogState,
    selectFullscreenRecordType,
    selectParentRecord,
    selectStoryRecord,
    selectAssignmentRecord,
    selectRundownRecord,
    <T extends StorytellingRecord>(
        newRecordState: FullscreenNewRecordState,
        type: number,
        topic: StorytellingTopicRecord,
        story: StorytellingStoryRecord,
        assignment: StorytellingAssignmentRecord,
        rundown: StorytellingRundownRecord
    ): T => {
        if (newRecordState?.record) {
            let newRecord = newRecordState.record as T;
            if (newRecordState?.selectedStoryIndex > -1) {
                newRecord = (newRecordState.record as StorytellingTopicRecord).stories[newRecordState.selectedStoryIndex] as T;
            }
            newRecord.isNewRecord = true;
            return newRecord;
        }
        switch (type) {
            case ObjectTypes.TOPIC:
                return topic as T;
            case ObjectTypes.ASSIGNMENT:
                return assignment as T;
            case ObjectTypes.SCRIPT:
                return story as T;
            case ObjectTypes.RUNDOWN:
                return rundown as T;
            default:
                return null;
        }
    }
);

export const selectCurrentTopicOrRundownRecord = createSelector(selectCurrentRecord, record => {
    if (record?.Type === ObjectTypes.TOPIC || record?.Type === ObjectTypes.RUNDOWN) {
        return record as StorytellingCollectionRecord;
    }
    return null;
});

export const selectCurrentRecordType = createSelector(selectCurrentRecord, record => {
    return record?.Type;
});

export const selectIsStoryLoose = createSelector(selectStoryRecord, story => {
    return !story?.PSTStoryTopicGUID && !story?.Path?.isInCollection;
});

export const selectCurrentStoryParentTopic = createSelector(selectCurrentRecord, selectTopicsWithAssignmentsAndStories, selectFullscreenNewRecordDialogState, (record, topics, newRecordState) => {
    if (newRecordState?.record) {
        if (newRecordState.record && newRecordState.record.Type === ObjectTypes.SCRIPT) {
            return null;
        }
        if (newRecordState.record && newRecordState.record.Type === ObjectTypes.TOPIC) {
            return newRecordState.record as StorytellingTopicRecord;
        }
    } else {
        if (record?.Type === ObjectTypes.SCRIPT) {
            return (record as StorytellingStoryRecord)?.PSTStoryTopicGUID ? topics.get((record as StorytellingStoryRecord)?.PSTStoryTopicGUID) : null;
        }
        if (record?.Type === ObjectTypes.TOPIC) {
            return record as StorytellingTopicRecord;
        }
    }
    return null;
});

export const selectCurrentStoryRelatives = createSelector(selectFullscreenRecordType, selectCurrentStoryParentTopic, (type, parentTopic) => {
    if (type === ObjectTypes.SCRIPT) {
        return parentTopic?.stories || [];
    }
    return null;
});

export const selectCurrentStoryIndex = createSelector(selectCurrentRecord, selectCurrentStoryParentTopic, (record, parentTopic) => {
    if (record?.Type === ObjectTypes.SCRIPT) {
        return parentTopic?.stories?.findIndex(story => story.Guid === record.Guid);
    }
    return -1;
});

export const selectFullscreenCurrentStory = createSelector(selectCurrentStoryIndex, selectCurrentRecord, (storyIndex, record) => {
    if (storyIndex > -1) {
        switch (record?.Type) {
            case ObjectTypes.TOPIC:
                return (record as StorytellingTopicRecord).stories[storyIndex] as StorytellingStoryRecord;
            case ObjectTypes.RUNDOWN:
                return (record as StorytellingRundownRecord).children[storyIndex] as StorytellingStoryRecord;
            case ObjectTypes.SCRIPT:
                return record as StorytellingStoryRecord;
            default:
                console.log('unknown record type in selectFullscreenCurrentStory selector');
                break;
        }
    }
    return null;
});

export const selectRecordTemplate = createSelector(selectFullscreenState, _state => {
    // TODO
    return null;
    // return state.recordTemplate;
});

export const selectFullscreenCurrentViewRecordGuid = createSelector(selectFullscreenState, state => {
    return state.currentViewRecordGuid;
});

export const selectFullscreenCurrentViewRecord = createSelector(
    selectFullscreenCurrentViewRecordGuid,
    selectTopicRecordsMap,
    selectAllAssignmentsMap,
    selectAllStoriesMap,
    selectRundownRecordsMap,
    (guid, topics, assignments, stories, rundowns) => {
        return topics.get(guid) || assignments.get(guid) || stories.get(guid) || rundowns.get(guid);
    }
);

export const selectFullscreenPinnedItems = createSelector(selectFullscreenState, selectTopicRecordsMap, selectAllAssignmentsMap, selectStoryRecordsMap, (state, topics, assignments, stories) => {
    return state.pinnedItemGuids.map(guid => topics.get(guid) || assignments.get(guid) || stories.get(guid))?.filter(val => !!val) || [];
});

export const selectPinnedItemGuid = createSelector(selectFullscreenState, state => state.selectedPinnedItemGuid);

// Fullscreen search selectors
export const selectFullscreenSearchTerm = createSelector(selectFullscreenState, state => state.searchTerm);
export const selectFullscreenSearchItemGuids = createSelector(selectFullscreenState, selectListGuids, (state, listGuids) => {
    if (!state.searchTerm?.trim()?.length) {
        return listGuids || [];
    }
    return state.searchResultGuids || [];
});
export const selectSearchTreeDataSourcePair = createSelector(
    selectFullscreenSearchItemGuids,
    selectTopicRecordsMap,
    selectAllAssignmentsMap,
    selectAllStoriesMap,
    (listGuids, topicsMap, assignmentsMap, storiesMap) => {
        const treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>(getNodeLevel, getIsNodeExpandable, {
            trackBy: node => node.uniqueId,
        });
        const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);
        // Populates our flattened data into the tree control
        const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);

        const resultGroup = new ResultGroup<StorytellingRecord>();
        const topicGroupKey = new GroupKey('type_' + ObjectTypes.TOPIC, 'Topics');
        const rundownGroupKey = new GroupKey('type_' + ObjectTypes.RUNDOWN, 'Rundowns');
        const storyGroupKey = new GroupKey('type_' + ObjectTypes.SCRIPT, 'Stories');

        listGuids.forEach(guid => {
            const record = topicsMap.get(guid) || assignmentsMap.get(guid) || storiesMap.get(guid);
            switch (record?.Type) {
                case ObjectTypes.TOPIC:
                    resultGroup.addToGroups([topicGroupKey], record);
                    break;
                case ObjectTypes.RUNDOWN:
                    resultGroup.addToGroups([rundownGroupKey], record);
                    break;
                case ObjectTypes.SCRIPT:
                    resultGroup.addToGroups([storyGroupKey], record);
                    break;
            }
        });
        treeDataSource.data = getRecordsTreeLikeData(resultGroup as ResultGroup<StorytellingStoryRecord>);
        treeControl.expandAll();

        return { treeDataSource, treeControl };
    }
);
export const selectSearchTreeDataSource = createSelector(selectSearchTreeDataSourcePair, pair => pair?.treeDataSource);
export const selectSearchTreeControl = createSelector(selectSearchTreeDataSourcePair, pair => pair?.treeControl);

export const selectCurrentSearchItemHeights = createSelector(selectSearchTreeControl, treeControl => {
    if (!treeControl?.dataNodes) return [];
    const heights = treeControl.dataNodes.map(item => {
        switch (item.level) {
            case 0:
                return 36;
            default:
                return 32;
        }
    });
    return heights;
});

export const selectFullscreenSearchItems = createSelector(
    selectFullscreenSearchItemGuids,
    selectTopicRecordsMap,
    selectAllAssignmentsMap,
    selectAllStoriesMap,
    (listGuids, topicsMap, assignmentsMap, storiesMap) => {
        const topics = [];
        const assignments = [];
        const stories = [];
        listGuids.forEach(guid => {
            const record = topicsMap.get(guid) || assignmentsMap.get(guid) || storiesMap.get(guid);
            switch (record?.Type) {
                case ObjectTypes.TOPIC:
                    topics.push(record);
                    break;
                case ObjectTypes.ASSIGNMENT:
                    assignments.push(record);
                    break;
                case ObjectTypes.SCRIPT:
                    stories.push(record);
                    break;
            }
        });
        return {
            topics,
            assignments,
            stories,
        };
    }
);

// Sidecar selectors
const selectDefaultSidecarMenu = createSelector(selectCurrentRecord, selectCurrentStoryIndex, (_record, _storyIdx) => {
    // TODO
    // if (record instanceof StorytellingTopicRecord && assignment) {
    //     return SidecarMenu.ASSIGNMENTS;
    // } else if (record instanceof StorytellingRundownRecord && storyIdx > -1) {
    //     return SidecarMenu.STORY;
    // }
    return SidecarMenu.AP_AI_SEARCH;
    // if (record instanceof RundownRecord && storyIdx > -1) {
    //     return SidecarMenu.STORY;
    // }
    // return sidecarMenus.get(record?.type)?.[0] || SidecarMenu.ASSIGNMENTS;
});

const sidecarMenuGetter = (state: SidecarState, defaultMenu: SidecarMenu) => state.selected || defaultMenu;
const sidecarAssignmentListGetter = (state: SidecarState, assignmentsMap: Map<string, StorytellingAssignmentRecord>, fullscreenCurrentTopicRecord: StorytellingTopicRecord) => {
    let guids = [];
    if (state.selectedAssignmentListSearchType === SearchType.SAVED && state.currentAssignmentViewStateGuid === SYSTEM_VS_GUID) {
        guids = fullscreenCurrentTopicRecord?.children?.map(child => child?.Guid) || [];
    } else {
        guids = state.assignmentListGuids || [];
    }
    return guids.filter(guid => assignmentsMap.has(guid));
};

const sidecarStoryListGetter = (state: SidecarState, storiesMap: Map<string, StorytellingStoryRecord>, fullscreenParentRecord: StorytellingTopicRecord) => {
    let guids = [];
    if (state.selectedStoryListSearchType === SearchType.SAVED && state.currentStoryViewStateGuid === SYSTEM_VS_GUID) {
        if (fullscreenParentRecord.Type === ObjectTypes.TOPIC) {
            guids = fullscreenParentRecord?.stories?.map(story => story?.Guid) || [];
        }
        if (fullscreenParentRecord.Type === ObjectTypes.RUNDOWN) {
            guids = fullscreenParentRecord?.children?.map(story => story?.Guid).filter(guid => !!guid && guid !== '0') || [];
        }
    } else {
        guids = state.storyListGuids || [];
    }
    return guids.filter(guid => storiesMap.has(guid));
};
const sidecarSelectedAssignmentGetter = (state: SidecarState, assignments: Map<string, StorytellingAssignmentRecord>) => assignments.get(state.selectedAssignmentGuid);
const sidecarSelectedStoryGetter = (state: SidecarState, stories: Map<string, StorytellingStoryRecord>) => stories.get(state.selectedStoryGuid);
const sidecarSelectedTopicVSGetter = (state: SidecarState) => state.storedTopicViewStates?.find(vs => vs.guid === state.currentTopicViewStateGuid);
const sidecarSelectedRundownVSGetter = (state: SidecarState) => state.storedRundownViewStates?.find(vs => vs.guid === state.currentRundownViewStateGuid);
const sidecarSelectedAssignmentVSGetter = (state: SidecarState) => state.storedAssignmentViewStates?.find(vs => vs.guid === state.currentAssignmentViewStateGuid);
const sidecarSelectedStoryVSGetter = (state: SidecarState) => state.storedStoryViewStates?.find(vs => vs.guid === state.currentStoryViewStateGuid);
const sidecarSelectedRundownGetter = (state: SidecarState, rundowns: Map<string, StorytellingRundownRecord>) => rundowns.get(state.selectedRundownGuid);
const sidecarSelectedTopicGetter = (state: SidecarState, rundowns: Map<string, StorytellingTopicRecord>) => rundowns.get(state.selectedTopicGuid);

// const sidecarAssignmentListTotalCountGetter = (state: SidecarState, fullscreenCurrentTopicRecord: StorytellingTopicRecord) => sidecarAssignmentListGetter(state, fullscreenCurrentTopicRecord)?.length;
// const sidecarStoryListTotalCountGetter = (state: SidecarState) => sidecarStoryListGetter(state)?.length;
// const sidecarSelectedAssignmentIndexGetter = (state: SidecarState, fullscreenCurrentTopicRecord: StorytellingTopicRecord) =>
//     sidecarAssignmentListGetter(state, fullscreenCurrentTopicRecord)?.indexOf(state.selectedAssignmentGuid);
// const sidecarSelectedStoryIndexGetter = (state: SidecarState) => sidecarStoryListGetter(state)?.indexOf(state.selectedStoryGuid);

export const selectCurrentSidecar = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectDefaultSidecarMenu, sidecarMenuGetter);
export const selectCurrentSidecarAssignmentList = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectAllAssignmentsMap, selectParentRecord, sidecarAssignmentListGetter);
export const selectCurrentSidecarStoryList = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectAllStoriesMap, selectParentRecord, sidecarStoryListGetter);
export const selectCurrentSidecarSelectedAssignment = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectFullscreenAssignmentsMap, sidecarSelectedAssignmentGetter);
export const selectCurrentSidecarSelectedStory = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectFullscreenStoriesMap, sidecarSelectedStoryGetter);
export const selectCurrentSidecarSelectedRundown = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectRundownsWithStories, sidecarSelectedRundownGetter);
export const selectCurrentSidecarSelectedTopic = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectTopicRecordsMap, sidecarSelectedTopicGetter);
export const selectCurrentSidecarAssignmentListTotalCount = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) => {
    return createSelector(selectCurrentSidecarAssignmentList(stateSelector), assignmentList => {
        return assignmentList?.length;
    });
};
export const selectCurrentSidecarAssignmentIndex = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectCurrentSidecarAssignmentList(stateSelector), (state, assignmentList) => {
        return assignmentList?.indexOf(state.selectedAssignmentGuid);
    });
export const selectCurrentSidecarStoryListTotalCount = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) => {
    return createSelector(selectCurrentSidecarStoryList(stateSelector), storiesList => {
        return storiesList?.length;
    });
};
export const selectCurrentSidecarStoryIndex = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, selectCurrentSidecarStoryList(stateSelector), (state, storiesList) => {
        return storiesList?.indexOf(state.selectedStoryGuid);
    });
export const selectCurrentSidecarTopicVS = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) => createSelector(stateSelector, sidecarSelectedTopicVSGetter);
export const selectCurrentSidecarRundownVS = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) => createSelector(stateSelector, sidecarSelectedRundownVSGetter);
export const selectCurrentSidecarAssignmentVS = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) =>
    createSelector(stateSelector, sidecarSelectedAssignmentVSGetter);
export const selectCurrentSidecarStoryVS = (stateSelector: MemoizedSelector<object, SidecarState, DefaultProjectorFn<SidecarState>>) => createSelector(stateSelector, sidecarSelectedStoryVSGetter);

// Main sidecar selectors
const selectMainSidecarState = createFeatureSelector<SidecarState>(FullscreenLayoutAreas.SIDECAR_MAIN);
export const selectCurrentMainSidecar = selectCurrentSidecar(selectMainSidecarState);
export const selectSidecarMainCurrentAssignmentList = selectCurrentSidecarAssignmentList(selectMainSidecarState);
export const selectSidecarMainCurrentStoryList = selectCurrentSidecarStoryList(selectMainSidecarState);
export const selectSidecarMainCurrentSelectedAssignment = selectCurrentSidecarSelectedAssignment(selectMainSidecarState);
export const selectSidecarMainCurrentSelectedRundown = selectCurrentSidecarSelectedRundown(selectMainSidecarState);
export const selectSidecarMainCurrentSelectedStory = selectCurrentSidecarSelectedStory(selectMainSidecarState);
export const selectSidecarMainCurrentAssignmentListTotalCount = selectCurrentSidecarAssignmentListTotalCount(selectMainSidecarState);
export const selectSidecarMainCurrentStoryListTotalCount = selectCurrentSidecarStoryListTotalCount(selectMainSidecarState);
export const selectSidecarMainCurrentAssignmentIndex = selectCurrentSidecarAssignmentIndex(selectMainSidecarState);
export const selectSidecarMainCurrentStoryIndex = selectCurrentSidecarStoryIndex(selectMainSidecarState);
export const selectSidecarMainCurrentTopicViewState = selectCurrentSidecarTopicVS(selectMainSidecarState);
export const selectSidecarMainCurrentRundownViewState = selectCurrentSidecarRundownVS(selectMainSidecarState);
export const selectSidecarMainCurrentAssignmentViewState = selectCurrentSidecarAssignmentVS(selectMainSidecarState);
export const selectSidecarMainCurrentSelectedTopic = selectCurrentSidecarSelectedTopic(selectMainSidecarState);
export const selectSidecarMainCurrentStoryViewState = selectCurrentSidecarStoryVS(selectMainSidecarState);
export const selectSidecarMainSelectedMosPlugin = createSelector(
    selectMainSidecarState,
    selectMosDevices,
    (sidecarState: SidecarState, mosItems) => mosItems?.find(mosItem => mosItem.ID === sidecarState.selectedMosItemID) ?? mosItems?.[0]
);
export const selectSidecarMainCurrentAssignmentListSearchType = createSelector(selectMainSidecarState, (sidecarState: SidecarState) => sidecarState.selectedAssignmentListSearchType);
export const selectSidecarMainCurrentStoryListSearchType = createSelector(selectMainSidecarState, (sidecarState: SidecarState) => sidecarState.selectedStoryListSearchType);
export const selectSidecarMainCurrentTopicListSearchType = createSelector(selectMainSidecarState, (sidecarState: SidecarState) => sidecarState.selectedTopicListSearchType);
export const selectSidecarMainCurrentRundownListSearchType = createSelector(selectMainSidecarState, (sidecarState: SidecarState) => sidecarState.selectedRundownListSearchType);

// Second sidecar selectors
const selectSecondSidecarState = createFeatureSelector<SidecarState>(FullscreenLayoutAreas.SIDECAR_SECOND);
export const selectCurrentSecondSidecar = selectCurrentSidecar(selectSecondSidecarState);
export const selectSidecarSecondCurrentAssignmentList = selectCurrentSidecarAssignmentList(selectSecondSidecarState);
export const selectSidecarSecondCurrentStoryList = selectCurrentSidecarStoryList(selectSecondSidecarState);
export const selectSidecarSecondCurrentSelectedAssignment = selectCurrentSidecarSelectedAssignment(selectSecondSidecarState);
export const selectSidecarSecondCurrentSelectedRundown = selectCurrentSidecarSelectedRundown(selectSecondSidecarState);
export const selectSidecarSecondCurrentSelectedStory = selectCurrentSidecarSelectedStory(selectSecondSidecarState);
export const selectSidecarSecondCurrentAssignmentListTotalCount = selectCurrentSidecarAssignmentListTotalCount(selectSecondSidecarState);
export const selectSidecarSecondCurrentStoryListTotalCount = selectCurrentSidecarStoryListTotalCount(selectSecondSidecarState);
export const selectSidecarSecondCurrentAssignmentIndex = selectCurrentSidecarAssignmentIndex(selectSecondSidecarState);
export const selectSidecarSecondCurrentStoryIndex = selectCurrentSidecarStoryIndex(selectSecondSidecarState);
export const selectSidecarSecondCurrentTopicViewState = selectCurrentSidecarTopicVS(selectSecondSidecarState);
export const selectSidecarSecondCurrentRundownViewState = selectCurrentSidecarRundownVS(selectSecondSidecarState);
export const selectSidecarSecondCurrentAssignmentViewState = selectCurrentSidecarAssignmentVS(selectSecondSidecarState);
export const selectSidecarSecondCurrentSelectedTopic = selectCurrentSidecarSelectedTopic(selectSecondSidecarState);
export const selectSidecarSecondCurrentStoryViewState = selectCurrentSidecarStoryVS(selectSecondSidecarState);
export const selectSidecarSecondSelectedMosPlugin = createSelector(
    selectSecondSidecarState,
    selectMosDevices,
    (sidecarState: SidecarState, mosItems) => mosItems?.find(mosItem => mosItem.ID === sidecarState.selectedMosItemID) ?? mosItems?.[0]
);
export const selectSidecarSecondCurrentAssignmentListSearchType = createSelector(selectSecondSidecarState, (sidecarState: SidecarState) => sidecarState.selectedAssignmentListSearchType);
export const selectSidecarSecondCurrentStoryListSearchType = createSelector(selectSecondSidecarState, (sidecarState: SidecarState) => sidecarState.selectedStoryListSearchType);
export const selectSidecarSecondCurrentTopicListSearchType = createSelector(selectSecondSidecarState, (sidecarState: SidecarState) => sidecarState.selectedTopicListSearchType);
export const selectSidecarSecondCurrentRundownListSearchType = createSelector(selectSecondSidecarState, (sidecarState: SidecarState) => sidecarState.selectedRundownListSearchType);

// Third sidecar selectors
const selectThirdSidecarState = createFeatureSelector<SidecarState>(FullscreenLayoutAreas.SIDECAR_THIRD);
export const selectCurrentThirdSidecar = selectCurrentSidecar(selectThirdSidecarState);
export const selectSidecarThirdCurrentAssignmentList = selectCurrentSidecarAssignmentList(selectThirdSidecarState);
export const selectSidecarThirdCurrentStoryList = selectCurrentSidecarStoryList(selectThirdSidecarState);
export const selectSidecarThirdCurrentSelectedAssignment = selectCurrentSidecarSelectedAssignment(selectThirdSidecarState);
export const selectSidecarThirdCurrentSelectedRundown = selectCurrentSidecarSelectedRundown(selectThirdSidecarState);
export const selectSidecarThirdCurrentSelectedStory = selectCurrentSidecarSelectedStory(selectThirdSidecarState);
export const selectSidecarThirdCurrentAssignmentListTotalCount = selectCurrentSidecarAssignmentListTotalCount(selectThirdSidecarState);
export const selectSidecarThirdCurrentStoryListTotalCount = selectCurrentSidecarStoryListTotalCount(selectThirdSidecarState);
export const selectSidecarThirdCurrentAssignmentIndex = selectCurrentSidecarAssignmentIndex(selectThirdSidecarState);
export const selectSidecarThirdCurrentStoryIndex = selectCurrentSidecarStoryIndex(selectThirdSidecarState);
export const selectSidecarThirdCurrentTopicViewState = selectCurrentSidecarTopicVS(selectThirdSidecarState);
export const selectSidecarThirdCurrentRundownViewState = selectCurrentSidecarRundownVS(selectThirdSidecarState);
export const selectSidecarThirdCurrentAssignmentViewState = selectCurrentSidecarAssignmentVS(selectThirdSidecarState);
export const selectSidecarThirdCurrentSelectedTopic = selectCurrentSidecarSelectedTopic(selectThirdSidecarState);
export const selectSidecarThirdCurrentStoryViewState = selectCurrentSidecarStoryVS(selectThirdSidecarState);
export const selectSidecarThirdSelectedMosPlugin = createSelector(
    selectThirdSidecarState,
    selectMosDevices,
    (sidecarState: SidecarState, mosItems) => mosItems?.find(mosItem => mosItem.ID === sidecarState.selectedMosItemID) ?? mosItems?.[0]
);
export const selectSidecarThirdCurrentAssignmentListSearchType = createSelector(selectThirdSidecarState, (sidecarState: SidecarState) => sidecarState.selectedAssignmentListSearchType);
export const selectSidecarThirdCurrentStoryListSearchType = createSelector(selectThirdSidecarState, (sidecarState: SidecarState) => sidecarState.selectedStoryListSearchType);
export const selectSidecarThirdCurrentTopicListSearchType = createSelector(selectThirdSidecarState, (sidecarState: SidecarState) => sidecarState.selectedTopicListSearchType);
export const selectSidecarThirdCurrentRundownListSearchType = createSelector(selectThirdSidecarState, (sidecarState: SidecarState) => sidecarState.selectedRundownListSearchType);

// Fourth sidecar selectors
const selectFourthSidecarState = createFeatureSelector<SidecarState>(FullscreenLayoutAreas.SIDECAR_FOURTH);
export const selectCurrentFourthSidecar = selectCurrentSidecar(selectFourthSidecarState);
export const selectSidecarFourthCurrentAssignmentList = selectCurrentSidecarAssignmentList(selectFourthSidecarState);
export const selectSidecarFourthCurrentStoryList = selectCurrentSidecarStoryList(selectFourthSidecarState);
export const selectSidecarFourthCurrentSelectedAssignment = selectCurrentSidecarSelectedAssignment(selectFourthSidecarState);
export const selectSidecarFourthCurrentSelectedRundown = selectCurrentSidecarSelectedRundown(selectFourthSidecarState);
export const selectSidecarFourthCurrentSelectedStory = selectCurrentSidecarSelectedStory(selectFourthSidecarState);
export const selectSidecarFourthCurrentAssignmentListTotalCount = selectCurrentSidecarAssignmentListTotalCount(selectFourthSidecarState);
export const selectSidecarFourthCurrentStoryListTotalCount = selectCurrentSidecarStoryListTotalCount(selectFourthSidecarState);
export const selectSidecarFourthCurrentAssignmentIndex = selectCurrentSidecarAssignmentIndex(selectFourthSidecarState);
export const selectSidecarFourthCurrentStoryIndex = selectCurrentSidecarStoryIndex(selectFourthSidecarState);
export const selectSidecarFourthCurrentTopicViewState = selectCurrentSidecarTopicVS(selectFourthSidecarState);
export const selectSidecarFourthCurrentRundownViewState = selectCurrentSidecarRundownVS(selectFourthSidecarState);
export const selectSidecarFourthCurrentAssignmentViewState = selectCurrentSidecarAssignmentVS(selectFourthSidecarState);
export const selectSidecarFourthCurrentSelectedTopic = selectCurrentSidecarSelectedTopic(selectFourthSidecarState);
export const selectSidecarFourthCurrentStoryViewState = selectCurrentSidecarStoryVS(selectFourthSidecarState);
export const selectSidecarFourthSelectedMosPlugin = createSelector(
    selectFourthSidecarState,
    selectMosDevices,
    (sidecarState: SidecarState, mosItems) => mosItems?.find(mosItem => mosItem.ID === sidecarState.selectedMosItemID) ?? mosItems?.[0]
);
export const selectSidecarFourthCurrentAssignmentListSearchType = createSelector(selectFourthSidecarState, (sidecarState: SidecarState) => sidecarState.selectedAssignmentListSearchType);
export const selectSidecarFourthCurrentStoryListSearchType = createSelector(selectFourthSidecarState, (sidecarState: SidecarState) => sidecarState.selectedStoryListSearchType);
export const selectSidecarFourthCurrentTopicListSearchType = createSelector(selectFourthSidecarState, (sidecarState: SidecarState) => sidecarState.selectedTopicListSearchType);
export const selectSidecarFourthCurrentRundownListSearchType = createSelector(selectFourthSidecarState, (sidecarState: SidecarState) => sidecarState.selectedRundownListSearchType);

// common sidecar selectors
export const selectStoryRecordToEdit = createSelector(
    selectCurrentRecord,
    selectSidecarMainCurrentSelectedStory,
    selectSidecarSecondCurrentSelectedStory,
    selectSidecarThirdCurrentSelectedStory,
    selectSidecarFourthCurrentSelectedStory,
    (currentRecord, main, second, third, fourth) => {
        if (currentRecord?.Type === ObjectTypes.SCRIPT) {
            return currentRecord as StorytellingStoryRecord;
        }
        return main || second || third || fourth;
    }
);

export const selectSidecarMenuSelected = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectCurrentMainSidecar,
        selectCurrentSecondSidecar,
        selectCurrentThirdSidecar,
        selectCurrentFourthSidecar,
        (mainSidecarSelectedMenu, secondSidecarSelectedMenu, thirdSidecarSelectedMenu, fourthSidecarSelectedMenu) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSidecarSelectedMenu;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSidecarSelectedMenu;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSidecarSelectedMenu;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSidecarSelectedMenu;
            }
            // TODO: add fallback - fallback will be based on the fullscreen record type.
            // TODO: eventually we should have an array of sidecar items for each record type????
            console.log('No sidecar selected');
            return SidecarMenu.ASSIGNMENTS;
        }
    );

export const selectSidecarMenus = createSelector(selectFullscreenState, _state => {
    // return sidecarMenus.get(state.record?.Type);
    // TODO
    return sidecarMenus.get(ObjectTypes.TOPIC);
});

export const selectSidecarAssignmentList = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentAssignmentList,
        selectSidecarSecondCurrentAssignmentList,
        selectSidecarThirdCurrentAssignmentList,
        selectSidecarFourthCurrentAssignmentList,
        (mainSidecarAssignmentList, secondSidecarAssignmentList, thirdSidecarAssignmentList, fourthSidecarAssignmentList) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSidecarAssignmentList;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSidecarAssignmentList;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSidecarAssignmentList;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSidecarAssignmentList;
            }
            return [];
        }
    );

export const selectSidecarSelectedAssignment = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentSelectedAssignment,
        selectSidecarSecondCurrentSelectedAssignment,
        selectSidecarThirdCurrentSelectedAssignment,
        selectSidecarFourthCurrentSelectedAssignment,
        (mainSidecarSelectedAssignment, secondSidecarSelectedAssignment, thirdSidecarSelectedAssignment, fourthSidecarSelectedAssignment) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSidecarSelectedAssignment;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSidecarSelectedAssignment;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSidecarSelectedAssignment;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSidecarSelectedAssignment;
            }
            return null;
        }
    );

export const selectSidecarTotalAssignmentCount = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentAssignmentListTotalCount,
        selectSidecarSecondCurrentAssignmentListTotalCount,
        selectSidecarThirdCurrentAssignmentListTotalCount,
        selectSidecarFourthCurrentAssignmentListTotalCount,
        (mainCount, secondCount, thirdCount, fourthCount) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainCount;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondCount;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdCount;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthCount;
            }
            return 0;
        }
    );

export const selectSidecarSelectedAssignmentIndex = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentAssignmentIndex,
        selectSidecarSecondCurrentAssignmentIndex,
        selectSidecarThirdCurrentAssignmentIndex,
        selectSidecarFourthCurrentAssignmentIndex,
        (mainIndex, secondIndex, thirdIndex, fourthIndex) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainIndex;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondIndex;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdIndex;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthIndex;
            }
            return -1;
        }
    );

export const selectAssignmentIsSelected = (assignmentGuid: string) =>
    createSelector(
        selectSidecarMainCurrentSelectedAssignment,
        selectSidecarSecondCurrentSelectedAssignment,
        selectSidecarThirdCurrentSelectedAssignment,
        selectSidecarFourthCurrentSelectedAssignment,
        (main, second, third, fourth) => {
            if (assignmentGuid == null) {
                return false;
            }
            return main?.Guid === assignmentGuid || second?.Guid === assignmentGuid || third?.Guid === assignmentGuid || fourth?.Guid === assignmentGuid;
        }
    );

export const selectSidecarCurrentTopicVS = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentTopicViewState,
        selectSidecarSecondCurrentTopicViewState,
        selectSidecarThirdCurrentTopicViewState,
        selectSidecarFourthCurrentTopicViewState,
        (main, second, third, fourth) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return main;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return second;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return third;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourth;
            }
            return null;
        }
    );

export const selectTopics = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, selectTopicRecordsMap, (main, second, third, fourth, topicsMap) => {
        let listGuids = [];
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                listGuids = main.topicListGuids;
                break;
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                listGuids = second.topicListGuids;
                break;
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                listGuids = third.topicListGuids;
                break;
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                listGuids = fourth.topicListGuids;
                break;
            default:
                break;
        }

        return listGuids?.map(guid => topicsMap.get(guid)).filter(topic => !!topic) || [];
    });

export const selectSavedTopicViewStates = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, (main, second, third, fourth) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return main.storedTopicViewStates;
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return second.storedTopicViewStates;
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return third.storedTopicViewStates;
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return fourth.storedTopicViewStates;
        }
    });

export const selectSavedRundownViewStates = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, (main, second, third, fourth) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return main.storedRundownViewStates;
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return second.storedRundownViewStates;
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return third.storedRundownViewStates;
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return fourth.storedRundownViewStates;
        }
    });

export const selectRundowns = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, selectRundownsWithStories, (main, second, third, fourth, rundownsMap) => {
        let listGuids = [];
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                listGuids = main.rundownListGuids;
                break;
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                listGuids = second.rundownListGuids;
                break;
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                listGuids = third.rundownListGuids;
                break;
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                listGuids = fourth.rundownListGuids;
                break;
            default:
                break;
        }
        return listGuids?.map(guid => rundownsMap.get(guid)).filter(rundown => !!rundown) || [];
    });

export const selectSidecarCurrentRundownVS = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentRundownViewState,
        selectSidecarSecondCurrentRundownViewState,
        selectSidecarThirdCurrentRundownViewState,
        selectSidecarFourthCurrentRundownViewState,
        (main, second, third, fourth) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return main;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return second;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return third;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourth;
            }
            return null;
        }
    );

export const selectSavedAssignmentViewStates = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, (main, second, third, fourth) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return main.storedAssignmentViewStates;
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return second.storedAssignmentViewStates;
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return third.storedAssignmentViewStates;
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return fourth.storedAssignmentViewStates;
        }
    });

export const selectSidecarCurrentAssignmentVS = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentAssignmentViewState,
        selectSidecarSecondCurrentAssignmentViewState,
        selectSidecarThirdCurrentAssignmentViewState,
        selectSidecarFourthCurrentAssignmentViewState,
        (main, second, third, fourth) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return main;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return second;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return third;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourth;
            }
            return null;
        }
    );

export const selectSidecarAssignments = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentAssignmentList,
        selectSidecarSecondCurrentAssignmentList,
        selectSidecarThirdCurrentAssignmentList,
        selectSidecarFourthCurrentAssignmentList,
        selectAllAssignmentsMap,
        (main, second, third, fourth, assignmentsMap) => {
            let listGuids = [];
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    listGuids = main;
                    break;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    listGuids = second;
                    break;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    listGuids = third;
                    break;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    listGuids = fourth;
                    break;
                default:
                    break;
            }

            return listGuids?.map(guid => assignmentsMap.get(guid)).filter(topic => !!topic) || [];
        }
    );

export const selectSavedStoryViewStates = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, (main, second, third, fourth) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return main.storedStoryViewStates;
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return second.storedStoryViewStates;
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return third.storedStoryViewStates;
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return fourth.storedStoryViewStates;
        }
    });

export const selectSidecarCurrentStoryVS = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentStoryViewState,
        selectSidecarSecondCurrentStoryViewState,
        selectSidecarThirdCurrentStoryViewState,
        selectSidecarFourthCurrentStoryViewState,
        (main, second, third, fourth) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return main;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return second;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return third;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourth;
            }
            return null;
        }
    );
export const selectSidecarSelectedRundown = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentSelectedRundown,
        selectSidecarSecondCurrentSelectedRundown,
        selectSidecarThirdCurrentSelectedRundown,
        selectSidecarFourthCurrentSelectedRundown,
        (mainSidecarSelectedRundown, secondSidecarSelectedRundown, thirdSidecarSelectedRundown, fourthSidecarSelectedRundown) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSidecarSelectedRundown;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSidecarSelectedRundown;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSidecarSelectedRundown;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSidecarSelectedRundown;
            }
            return null;
        }
    );

export const selectSidecarSelectedTopic = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentSelectedTopic,
        selectSidecarSecondCurrentSelectedTopic,
        selectSidecarThirdCurrentSelectedTopic,
        selectSidecarFourthCurrentSelectedTopic,
        (mainSidecarSelectedTopic, secondSidecarSelectedTopic, thirdSidecarSelectedTopic, fourthSidecarSelectedTopic) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSidecarSelectedTopic;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSidecarSelectedTopic;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSidecarSelectedTopic;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSidecarSelectedTopic;
            }
            return null;
        }
    );
export const selectSidecarStories = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentStoryList,
        selectSidecarSecondCurrentStoryList,
        selectSidecarThirdCurrentStoryList,
        selectSidecarFourthCurrentStoryList,
        selectAllStoriesMap,
        (main, second, third, fourth, storiesMap) => {
            let listGuids = [];
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    listGuids = main;
                    break;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    listGuids = second;
                    break;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    listGuids = third;
                    break;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    listGuids = fourth;
                    break;
                default:
                    listGuids = [];
                    break;
            }

            return listGuids?.map(guid => storiesMap.get(guid)).filter(story => !!story) || [];
        }
    );

export const selectSidecarCurrentStory = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, selectAllStoriesMap, (main, second, third, fourth, stories) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return stories.get(main.selectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return stories.get(second.selectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return stories.get(third.selectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return stories.get(fourth.selectedStoryGuid);
        }
        return null;
    });

export const selectSidecarSelectedStoryIndex = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentStoryIndex,
        selectSidecarSecondCurrentStoryIndex,
        selectSidecarThirdCurrentStoryIndex,
        selectSidecarFourthCurrentStoryIndex,
        (mainIndex, secondIndex, thirdIndex, fourthIndex) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainIndex;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondIndex;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdIndex;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthIndex;
            }
            return -1;
        }
    );

export const selectSidecarTotalStoryCount = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentStoryListTotalCount,
        selectSidecarSecondCurrentStoryListTotalCount,
        selectSidecarThirdCurrentStoryListTotalCount,
        selectSidecarFourthCurrentStoryListTotalCount,
        (mainCount, secondCount, thirdCount, fourthCount) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainCount;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondCount;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdCount;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthCount;
            }
            return 0;
        }
    );

export const selectSidecarCurrentTopicSelectedStory = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, selectAllStoriesMap, (main, second, third, fourth, stories) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return stories.get(main.currentTopicSelectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return stories.get(second.currentTopicSelectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return stories.get(third.currentTopicSelectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return stories.get(fourth.currentTopicSelectedStoryGuid);
        }
        return null;
    });

export const selectSidecarCurrentTopicStoryGuids = (key: FullscreenLayoutAreas) =>
    createSelector(selectSidecarSelectedTopic(key), selectAllStoriesMap, (topic, stories) => {
        return (
            topic?.stories
                ?.filter(story => stories.has(story?.Guid))
                .filter(story => !!story)
                .map(story => story?.Guid) || []
        );
    });

export const selectSidecarCurrentTopicSelectedAssignment = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, selectAllAssignmentsMap, (main, second, third, fourth, assignments) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return assignments.get(main.currentTopicSelectedAssignmentGuid);
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return assignments.get(second.currentTopicSelectedAssignmentGuid);
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return assignments.get(third.currentTopicSelectedAssignmentGuid);
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return assignments.get(fourth.currentTopicSelectedAssignmentGuid);
        }
        return null;
    });

export const selectSidecarCurrentRundownSelectedStory = (key: FullscreenLayoutAreas) =>
    createSelector(selectMainSidecarState, selectSecondSidecarState, selectThirdSidecarState, selectFourthSidecarState, selectAllStoriesMap, (main, second, third, fourth, stories) => {
        switch (key) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return stories.get(main.currentRundownSelectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return stories.get(second.currentRundownSelectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return stories.get(third.currentRundownSelectedStoryGuid);
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return stories.get(fourth.currentRundownSelectedStoryGuid);
        }
        return null;
    });
// EoF Sidecar selectors

export const selectAnyUnsavedChanges = createSelector(
    selectFullscreenState,
    selectMainSidecarState,
    selectSecondSidecarState,
    selectThirdSidecarState,
    selectFourthSidecarState,
    (fullscreenState, mainSidecarState, secondSidecarState, thirdSidecarState, fourthSidecarState) =>
        fullscreenState.recordHasUnsavedChanges ||
        mainSidecarState.selectedStoryHasUnsavedChanges ||
        secondSidecarState.selectedStoryHasUnsavedChanges ||
        thirdSidecarState.selectedStoryHasUnsavedChanges ||
        fourthSidecarState.selectedStoryHasUnsavedChanges
);

export const selectSidecarSelectedMosPlugin = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainSelectedMosPlugin,
        selectSidecarSecondSelectedMosPlugin,
        selectSidecarThirdSelectedMosPlugin,
        selectSidecarFourthSelectedMosPlugin,
        (mainMosItem, secondMosItem, thirdMosItem, fourthMosItem) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainMosItem;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondMosItem;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdMosItem;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthMosItem;
            }
            return null;
        }
    );

export const selectSidecarSelectedAssignmentListSearchType = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentAssignmentListSearchType,
        selectSidecarSecondCurrentAssignmentListSearchType,
        selectSidecarThirdCurrentAssignmentListSearchType,
        selectSidecarFourthCurrentAssignmentListSearchType,
        (mainSearchType, secondSearchType, thirdSearchType, fourthSearchType) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSearchType;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSearchType;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSearchType;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSearchType;
            }
            return null;
        }
    );

export const selectSidecarSelectedRundownListSearchType = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentRundownListSearchType,
        selectSidecarSecondCurrentRundownListSearchType,
        selectSidecarThirdCurrentRundownListSearchType,
        selectSidecarFourthCurrentRundownListSearchType,
        (mainSearchType, secondSearchType, thirdSearchType, fourthSearchType) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSearchType;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSearchType;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSearchType;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSearchType;
            }
            return null;
        }
    );

export const selectSidecarSelectedStoryListSearchType = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentStoryListSearchType,
        selectSidecarSecondCurrentStoryListSearchType,
        selectSidecarThirdCurrentStoryListSearchType,
        selectSidecarFourthCurrentStoryListSearchType,
        (mainSearchType, secondSearchType, thirdSearchType, fourthSearchType) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSearchType;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSearchType;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSearchType;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSearchType;
            }
            return null;
        }
    );

export const selectSidecarSelectedTopicListSearchType = (key: FullscreenLayoutAreas) =>
    createSelector(
        selectSidecarMainCurrentTopicListSearchType,
        selectSidecarSecondCurrentTopicListSearchType,
        selectSidecarThirdCurrentTopicListSearchType,
        selectSidecarFourthCurrentTopicListSearchType,
        (mainSearchType, secondSearchType, thirdSearchType, fourthSearchType) => {
            switch (key) {
                case FullscreenLayoutAreas.SIDECAR_MAIN:
                    return mainSearchType;
                case FullscreenLayoutAreas.SIDECAR_SECOND:
                    return secondSearchType;
                case FullscreenLayoutAreas.SIDECAR_THIRD:
                    return thirdSearchType;
                case FullscreenLayoutAreas.SIDECAR_FOURTH:
                    return fourthSearchType;
            }
            return null;
        }
    );
